<script setup lang="ts">
import { ref, computed } from 'vue'
import type { VueformSchema } from '@/types/form-builder-types'

const props = defineProps<{
  schema?: VueformSchema
}>()

// State
const formData = ref<Record<string, any>>({})
const showJson = ref(false)
const vueformRef = ref<any>(null)

// Computed
const hasFields = computed(() => {
  return props.schema && Object.keys(props.schema).length > 0
})

const formattedSchema = computed(() => {
  if (!props.schema) return '{}'
  return JSON.stringify(props.schema, null, 2)
})

// Methods
function handleSubmit() {
  alert('Form validation passed!\n\nSubmitted data:\n' + JSON.stringify(formData.value, null, 2))
}

function copyToClipboard() {
  if (typeof navigator !== 'undefined' && navigator.clipboard) {
    navigator.clipboard.writeText(formattedSchema.value)
  }
}
</script>

<template>
  <div class="form-preview">
    <!-- Header -->
    <div class="card">
      <div class="card-header d-flex justify-content-between align-items-center">
        <h3 class="mb-0">
          <i class="bi bi-eye me-2"></i>
          Form Preview
        </h3>
        <button
          type="button"
          class="bb-btn-secondary-cta btn-sm"
          @click="showJson = !showJson"
        >
          <i class="bi bi-code-slash me-1"></i>
          {{ showJson ? 'Show Form' : 'Show JSON' }}
        </button>
      </div>

      <div class="card-body">
        <!-- Empty State -->
        <div v-if="!hasFields" class="empty-state d-flex flex-column justify-content-center align-items-center text-center py-5">
          <i class="bi bi-eye-slash display-4 text-muted"></i>
          <p class="mt-3 text-muted fw-semibold">No Preview Available</p>
          <p class="text-muted small">Add fields to see a preview of your form</p>
        </div>

        <!-- JSON View -->
        <div v-else-if="showJson" class="json-view">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <small class="text-muted">Vueform Schema</small>
            <button
              type="button"
              class="bb-btn-secondary-cta btn-sm"
              @click="copyToClipboard"
            >
              <i class="bi bi-clipboard me-1"></i> Copy
            </button>
          </div>
          <pre class="bg-body-tertiary p-3 rounded"><code>{{ formattedSchema }}</code></pre>
        </div>

        <!-- Form Preview/Test -->
        <div v-else class="form-container">
          <Vueform
            ref="vueformRef"
            :schema="(schema as any)"
            v-model="formData"
            @submit="handleSubmit"
            :columns="{ label: 12, wrapper: 12 }"
          />

          <div class="d-grid gap-2 mt-4">
            <button type="button" class="bb-btn-primary-cta" @click="vueformRef?.submit()">
              <i class="bi bi-check-circle me-2"></i>
              Submit Form
            </button>
          </div>

          <!-- Form Data -->
          <div v-if="Object.keys(formData).length > 0" class="mt-3">
            <p class="text-muted small mb-1">Form Data:</p>
            <pre class="bg-body-tertiary p-2 rounded small"><code>{{ JSON.stringify(formData, null, 2) }}</code></pre>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.empty-state {
  min-height: 300px;
}

.json-view pre {
  max-height: 400px;
  overflow-y: auto;
  font-size: 0.875rem;
}

.json-view code {
  color: var(--bs-body-color);
}

.form-container {
  overflow-y: auto;
  width: 100%;
}

/*
 * Vueform's bootstrap theme generates vf-col-N classes via its columns()
 * function but ships no CSS rules for them. The layout uses CSS Grid with
 * grid-template-columns: repeat(12, ...) so each vf-col-N needs
 * grid-column: span N to actually occupy the right number of columns.
 */
:deep(.vf-col-0)  { grid-column: span 0; }
:deep(.vf-col-1)  { grid-column: span 1; }
:deep(.vf-col-2)  { grid-column: span 2; }
:deep(.vf-col-3)  { grid-column: span 3; }
:deep(.vf-col-4)  { grid-column: span 4; }
:deep(.vf-col-5)  { grid-column: span 5; }
:deep(.vf-col-6)  { grid-column: span 6; }
:deep(.vf-col-7)  { grid-column: span 7; }
:deep(.vf-col-8)  { grid-column: span 8; }
:deep(.vf-col-9)  { grid-column: span 9; }
:deep(.vf-col-10) { grid-column: span 10; }
:deep(.vf-col-11) { grid-column: span 11; }
:deep(.vf-col-12) { grid-column: span 12; }

.card-body {
  max-height: inherit;
  overflow-y: auto;
}
</style>
